import React, { useState, useEffect, useCallback } from "react";
import { CancelRequest } from "../../../services/AxiosInstance";
import { connect } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import Pagination from "../../constant/Pagination";
import TableLoaderEffect from "../../constant/TableLoader";
import DebounceSearchInput from "../../constant/DebounceSearchInput";
import { getAuditListApi } from "../../../services/AuditService";
import VersionStatus from "./VersionStatus";
import { StaticSelectInput } from "../../constant/SelectInput";

const AuditPage = ({ userPermission }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [data, setData] = useState([]);
  const [query, setSearchQuery] = useState("");
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState({ status: "" });
  const recordsPerPage = 25;
  const [searchParams] = useSearchParams();
  const status = searchParams.get("status");
  const navigate = useNavigate();

  useEffect(() => {
    if (status) {
      setFilter((prev) => ({ ...prev, status }));
      setCurrentPage(1);
      navigate("/audit", { replace: true });
    }
  }, [status, navigate]);

  const fetchData = useCallback(() => {
    setIsLoading(true);
    CancelRequest();
    getAuditListApi(currentPage, recordsPerPage, query, filter.status)
      .then((resp) => {
        setData(resp.data.data || []);
        setTotal(resp.data.total || 0);
      })
      .catch((error) => {
        console.error(error.response?.data?.message || "Failed to fetch data");
      })
      .finally(() => setIsLoading(false));
  }, [currentPage, query, filter]);

  useEffect(() => {
    fetchData();
  }, [fetchData, status]);

  const handlePagination = (page) => {
    setCurrentPage(page.selected + 1);
  };

  const handleSearchChange = (value) => {
    CancelRequest();
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleSelectChange = (name, value) => {
    setFilter((prev) => ({ ...prev, [name]: value }));
  };

  const handleAfterUpdate = (updatedData) => {
    setData((prevData) =>
      prevData.map((item) =>
        item.id === updatedData.id ? { ...item, ...updatedData } : item
      )
    );
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-xl-12">
          <div className="card">
            <div className="card-body p-0">
              <div className="table-header d-flex justify-content-between align-items-center">
                <h3 className="mb-0">Audit List</h3>
                <div className="table-left-header d-flex align-items-center">
                  <div className="me-3 mt-3">
                    <StaticSelectInput
                      optionsList={[
                        { label: "Pending", value: "pending" },
                        { label: "Approved", value: "approved" },
                        { label: "Rejected", value: "rejected" },
                      ]}
                      setParentInputValue={handleSelectChange}
                      name="status"
                      isClearable
                      placeholder="Select Status"
                    />
                  </div>
                  <div className="form-group me-3">
                    <DebounceSearchInput
                      handleSearchChange={handleSearchChange}
                    />
                  </div>
                  {userPermission?.create && (
                    <button
                      className="btn btn-primary mx-2"
                      onClick={() => navigate("/add-checklist")}
                    >
                      Add Audit
                    </button>
                  )}
                </div>
              </div>

              {/* Table Body */}
              <div className="table-responsive active-projects style-1 ItemsCheckboxSec shorting">
                <table
                  id="employees-tblwrapper"
                  className="table ItemsCheckboxSec dataTable no-footer mb-0"
                >
                  <thead>
                    <tr>
                      <th className="fw-bold">Title</th>
                      <th className="fw-bold">Created By</th>
                      <th className="fw-bold">Approved By</th>
                      <th className="fw-bold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      <TableLoaderEffect colLength={4} />
                    ) : data.length > 0 ? (
                      data.map((item) => (
                        <tr key={item.id}>
                          <td>
                            <span
                              className="text-primary fw-bold me-3 c-pointer"
                              onClick={() => navigate(`/audit/${item.id}`)}
                            >
                              {item?.title}
                            </span>
                          </td>
                          <td>
                            <div className="d-flex flex-column">
                              <strong>{item?.created_by?.name}</strong>
                              <span>{item?.created_by?.created_at}</span>
                            </div>
                          </td>
                          <td>
                            <div className="d-flex flex-column">
                              <strong>{item?.approved_by?.name}</strong>
                              <span>{item?.approved_by?.approved_at}</span>
                            </div>
                          </td>
                          <td>
                            <VersionStatus
                              data={item}
                              handleAfterUpdate={handleAfterUpdate}
                            />
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="text-center">
                          No records found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {!isLoading && total > 0 && (
                <Pagination
                  currentPage={currentPage}
                  recordsPerPage={recordsPerPage}
                  dataLength={total}
                  handlePagination={handlePagination}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  userPermission: state.auth.user?.audit_permission,
  user: state.auth.user,
});
export default connect(mapStateToProps)(AuditPage);
